import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_FAILURE_CONFIG,
  FAILURE_CONFIG_SERVICES,
  FAILURE_MODES,
  fetchFailureConfig,
  putFailureConfig,
  type FailureConfig,
  type FailureConfigService,
  type FailureMode,
} from "../failureConfig";
import {
  BASELINE_LOAD_RATES,
  DEFAULT_BASELINE_LOAD_CONFIG,
  fetchBaselineLoad,
  putBaselineLoad,
  type BaselineLoadConfig,
  type BaselineLoadRate,
} from "../baselineLoad";
import { signIn } from "../session";

type SaveState = { kind: "idle" } | { kind: "saving" } | { kind: "ok"; at: number } | { kind: "error"; message: string };

/**
 * Failure config (#262, ADR 0064) — absorbed from load-generator-ui's
 * "Failure config" tab (#150). Sets the **ambient, global** per-service
 * failure rates + mode via `GET` / `PUT /api/admin/failure-config` (behind
 * the BFF, cookie auth — ADR 0027 for the underlying SSM param). Unlike the
 * Run screen's scenario presets — which use `forceFailure` for one
 * deterministic outcome per run — this is the background mix that *all*
 * traffic runs at, baseline load included, until changed.
 */
export function FailureConfigScreen() {
  const [config, setConfig] = useState<FailureConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ kind: "idle" });

  const [baseline, setBaseline] = useState<BaselineLoadConfig | null>(null);
  const [baselineLoadError, setBaselineLoadError] = useState<string | null>(null);
  const [baselineSave, setBaselineSave] = useState<SaveState>({ kind: "idle" });

  const load = useCallback(async () => {
    setLoadError(null);
    const res = await fetchFailureConfig();
    if (res.kind === "config") {
      setConfig(res.body);
    } else if (res.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setConfig(DEFAULT_FAILURE_CONFIG);
      setLoadError(res.error);
    }
    setSave({ kind: "idle" });
  }, []);

  const loadBaseline = useCallback(async () => {
    setBaselineLoadError(null);
    const res = await fetchBaselineLoad();
    if (res.kind === "config") {
      setBaseline(res.body);
    } else if (res.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setBaseline(DEFAULT_BASELINE_LOAD_CONFIG);
      setBaselineLoadError(res.error);
    }
    setBaselineSave({ kind: "idle" });
  }, []);

  useEffect(() => {
    void load();
    void loadBaseline();
  }, [load, loadBaseline]);

  async function write(next: FailureConfig) {
    setConfig(next);
    setSave({ kind: "saving" });
    const res = await putFailureConfig(next);
    if (res.kind === "ok") {
      setConfig(res.body);
      setSave({ kind: "ok", at: Date.now() });
    } else if (res.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setSave({ kind: "error", message: res.error });
    }
  }

  async function writeBaseline(next: BaselineLoadConfig) {
    setBaseline(next);
    setBaselineSave({ kind: "saving" });
    const res = await putBaselineLoad(next);
    if (res.kind === "ok") {
      setBaseline(res.body);
      setBaselineSave({ kind: "ok", at: Date.now() });
    } else if (res.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setBaselineSave({ kind: "error", message: res.error });
    }
  }

  if (config === null) {
    return <p className="status-message">Loading current failure config…</p>;
  }

  const cfg = config;
  const setRate = (svc: FailureConfigService, pct: number) => setConfig({ ...cfg, [svc]: clamp01(pct / 100) });
  const canSave = save.kind !== "saving";

  return (
    <div className="failcfg">
      <p className="sub">
        The <strong>ambient</strong> failure mix — every order at every stage, the baseline-load heartbeat included
        (see its own control below), rolls against these until you change them. <strong>Global and persistent</strong>{" "}
        (stored in SSM, survives redeploys). This is <em>not</em> the Run screen's scenario presets: those force one
        deterministic outcome for a single run; <code>forceFailure</code> on an order still overrides whatever is set
        here.
      </p>

      {loadError && (
        <p className="hint warn">
          Couldn't read the current config ({loadError}) — showing the seed defaults. Needs the load-generator
          deployed with <code>FAILURE_CONFIG_PARAM</code> (#149).
        </p>
      )}

      <fieldset>
        <legend>Per-service failure rate</legend>
        {FAILURE_CONFIG_SERVICES.map((svc) => {
          const pct = Math.round(cfg[svc] * 100);
          return (
            <label key={svc} className="failcfg-slider">
              <span className="failcfg-name">{svc}</span>
              <input type="range" min={0} max={100} step={1} value={pct} onChange={(e) => setRate(svc, Number(e.target.value))} />
              <span className="failcfg-pct">{pct}%</span>
            </label>
          );
        })}
      </fieldset>

      <fieldset>
        <legend>Mode</legend>
        <label>
          how a failed invocation fails
          <select value={cfg.mode} onChange={(e) => setConfig({ ...cfg, mode: e.target.value as FailureMode })}>
            {FAILURE_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <p className="hint">
          <code>exception</code> → raise (retries + DLQ) · <code>timeout</code> → sleep past the deadline ·{" "}
          <code>partial</code> → emit the failure event and return.
        </p>
      </fieldset>

      <div className="actions">
        <button type="button" onClick={() => void write(cfg)} disabled={!canSave}>
          {save.kind === "saving" ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={() => void write(DEFAULT_FAILURE_CONFIG)} disabled={!canSave}>
          Reset to defaults
        </button>
        {save.kind === "ok" && <span className="hint ok">Saved — services pick it up within ~30s.</span>}
        {save.kind === "error" && <span className="hint warn">{save.message}</span>}
      </div>

      <section>
        <p className="sub">
          The <strong>baseline-load heartbeat</strong> — one canned order fired at order-service on a schedule, so
          the backend always has a fresh trace. Pause it or change its rate without a redeploy (#306). Reads/writes
          the <code>aws_scheduler_schedule.baseline_load</code> schedule directly — no separate SSM param.
        </p>

        {baselineLoadError && (
          <p className="hint warn">
            Couldn't read the current baseline-load state ({baselineLoadError}) — showing the seed defaults. Needs
            the load-generator deployed with <code>BASELINE_LOAD_SCHEDULE_NAME</code> (#306).
          </p>
        )}

        {baseline === null ? (
          <p className="status-message">Loading current baseline-load state…</p>
        ) : (
          <fieldset>
            <legend>Baseline load</legend>
            <label>
              <span>
                <input
                  type="checkbox"
                  checked={baseline.enabled}
                  onChange={(e) => setBaseline({ ...baseline, enabled: e.target.checked })}
                />{" "}
                Enabled
              </span>
            </label>
            <label>
              rate
              <select
                value={baseline.rate}
                onChange={(e) => setBaseline({ ...baseline, rate: e.target.value as BaselineLoadRate })}
              >
                {BASELINE_LOAD_RATES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <div className="actions">
              <button type="button" onClick={() => void writeBaseline(baseline)} disabled={baselineSave.kind === "saving"}>
                {baselineSave.kind === "saving" ? "Saving…" : "Save"}
              </button>
              {baselineSave.kind === "ok" && <span className="hint ok">Saved.</span>}
              {baselineSave.kind === "error" && <span className="hint warn">{baselineSave.message}</span>}
            </div>
          </fieldset>
        )}
      </section>
    </div>
  );
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}
